import {createSlice, isAnyOf, type SliceSelectors} from '@reduxjs/toolkit'
import {LOCATION_CHANGE} from 'redux-first-history'

import {applyAddress} from '@web/actions/deliveryManager'
import {getMerchantMerchantPenaltyStatus} from '@web/actions/fdsInfo'
import {restoreSavedState, restoreState} from '@web/actions/restoreState'
import {getAmlToken, getCartCount, getTradeAccountInfo} from '@web/actions/result'
import {getShoppingLiveStorage} from '@web/actions/shoppingLive'
import {getInitialResultSheetId} from '@web/helpers/path'
import {getInitialState, ResultState} from '@web/slices/result/slice'
import {LocationChangeAction} from '@web/slices/router'

import {MakeSliceOptions, ResultReducers} from './interfaces'

/** 주문완료 */
export default (options?: MakeSliceOptions) =>
    createSlice<ResultState, ResultReducers, 'result', SliceSelectors<ResultState>>({
        name: 'result',
        initialState: getInitialState(),
        reducers: {
            setMembershipPointLoadingState: (state, {payload}) => {
                state.isMembershipPointLoading = payload
            },
        },
        extraReducers: (builder) => {
            builder
                .addCase<typeof LOCATION_CHANGE, LocationChangeAction>(LOCATION_CHANGE, (state) => {
                    state.order.orderNo = getInitialResultSheetId()
                })
                .addCase(getMerchantMerchantPenaltyStatus.fulfilled, (state, {payload}) => {
                    Object.entries(payload).forEach(([merchantNo, penatyDetail]) => {
                        if (state.merchantsByMerchantNo[merchantNo]) {
                            state.merchantsByMerchantNo[merchantNo].penaltyStatus = penatyDetail.inspectionCode
                            state.merchantsByMerchantNo[merchantNo].abusingCode = penatyDetail.abuseCode
                        }
                    })
                })
                .addCase(getAmlToken.fulfilled, (state, {payload}) => {
                    state.amlToken = payload.amlToken
                })
                .addCase(getTradeAccountInfo.fulfilled, (state, {payload}) => {
                    state.tradeAccountInfo = payload
                })
                .addCase(getCartCount.fulfilled, (state, {payload}) => {
                    state.cartCount = payload
                })
                .addCase(getShoppingLiveStorage.fulfilled, (state, {payload}) => {
                    if (payload.shoppingData) {
                        const {shoppingLiveExtras, ...rest} = payload.shoppingData

                        state.shoppingLive = {
                            ...rest,
                            prdFrom: rest.prdFrom ?? [],
                            extras: shoppingLiveExtras,
                        }
                    }
                })
                .addCase(applyAddress.fulfilled, (state, {payload}) => {
                    if (payload) {
                        const {type, content} = payload || {}
                        if (type === 'deliveryInfo') {
                            const {isParticularInput, deliveryAddress, memos} = content

                            state.order.deliveryMemoParticularInput = isParticularInput

                            if (state.deliveryAddressBook) {
                                state.deliveryAddressBook.receiverName = deliveryAddress.receiverName
                                state.deliveryAddressBook.baseAddress = deliveryAddress.baseAddress
                                state.deliveryAddressBook.detailAddress = deliveryAddress.detailAddress ?? null
                                state.deliveryAddressBook.zipCode = deliveryAddress.zipCode
                                state.deliveryAddressBook.telNo1 = deliveryAddress.telNo1
                                state.deliveryAddressBook.telNo2 = deliveryAddress.telNo2 ?? null
                            }

                            state.products = memos.reduce((acc, {memo, productOrderNo}) => {
                                return acc.map((product) => {
                                    return {
                                        ...product,
                                        deliveryMemo:
                                            productOrderNo === product.productOrderNo ? memo : product.deliveryMemo,
                                        items: product.items.map((item) => {
                                            if (productOrderNo === item.productOrderNo) {
                                                return {
                                                    ...item,
                                                    deliveryMemo: memo,
                                                }
                                            }
                                            return item
                                        }),
                                    }
                                })
                            }, state.products)
                        }
                    }
                })

            options?.extraReducers?.(builder)
            builder.addMatcher(isAnyOf(restoreSavedState.fulfilled, restoreState), (state, {payload}) => {
                return payload.result ?? state
            })
        },
    })
